/**
 * Tells the SalunBD API which of our own apps is calling (the server's half is client-key.middleware.ts), and makes a double
 * tap safe.
 *
 * Wraps fetch so that every request to the API carries X-Client-Id and X-Client-Key. Requests to anything else (maps,
 * images, other sites) are left alone, so the key never goes where it should not. A key inside an app can be extracted,
 * so this keeps out bots and lets a leaked key be shut off; sign-in and the role checks are what protect the data.
 *
 * Every write also carries an Idempotency-Key (see idempotency.ts), so tapping Book or Confirm twice runs it once. If the
 * server answers that the same write is still running, this waits a moment and asks again, which returns the first
 * attempt's result: the second tap never shows an error for something that worked.
 *
 * Sign-up, sign-in and booking also carry proof of a real client: a Google Play Integrity token from the Android app
 * (playIntegrity.ts), or a Cloudflare Turnstile token from the website and Partner web (turnstile.ts).
 */
import { idempotencyKey, isStillRunning, pause } from './idempotency';
import { integrityActionFor, integrityHeaders } from './playIntegrity';
import { turnstileActionFor, turnstileHeaders } from './turnstile';

type FetchFn = typeof fetch;

export function createClientKeyFetch(nativeFetch: FetchFn, apiBase: string, clientId: string, clientKey: string): FetchFn {
  return async (input, init) => {
    const isRequest = typeof Request !== 'undefined' && input instanceof Request;
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
    if (!url.startsWith(apiBase)) return nativeFetch(input, init);

    const headers = new Headers(init?.headers ?? (isRequest ? (input as Request).headers : undefined));
    if (!headers.has('x-client-id')) headers.set('x-client-id', clientId);
    if (!headers.has('x-client-key')) headers.set('x-client-key', clientKey);

    const method = (init?.method ?? (isRequest ? (input as Request).method : 'GET')).toUpperCase();
    const isWrite = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';

    // Proof that this is the real app, for the few requests that ask for it. Each try needs its own (a token works once).
    const integrityAction = integrityActionFor(method, apiBase, url);
    const turnstileAction = turnstileActionFor(method, apiBase, url);
    const addIntegrity = async () => {
      if (integrityAction && !headers.has('x-integrity-token')) {
        const proof = await integrityHeaders(integrityAction, nativeFetch, apiBase, { 'x-client-id': clientId, 'x-client-key': clientKey });
        for (const [name, value] of Object.entries(proof)) headers.set(name, value);
      }
      if (turnstileAction && !headers.has('x-turnstile-token')) {
        const proof = await turnstileHeaders(turnstileAction);
        for (const [name, value] of Object.entries(proof)) headers.set(name, value);
      }
    };
    await addIntegrity();

    // A request object's body can be read once, so keep spare copies (one to read, one for a possible second try) before sending.
    const source = isRequest ? (input as Request) : null;
    const spare = isWrite && source ? [source.clone(), source.clone()] : [];

    let key: string | null = null;
    if (isWrite && !headers.has('idempotency-key')) {
      const type = headers.get('content-type') ?? '';
      const upload = type.includes('multipart/form-data') || (typeof FormData !== 'undefined' && init?.body instanceof FormData);
      // An upload's body cannot be compared (and is large), so it gets a random key; anything else is keyed by what it says.
      const body = upload ? new FormData() : typeof init?.body === 'string' ? init.body : source ? await source.clone().text().catch(() => '') : init?.body;
      key = idempotencyKey(method, url, body);
      if (key) headers.set('idempotency-key', key);
    }

    const send = (spareIndex: number) => {
      if (source) return nativeFetch(new Request(spare[spareIndex] ?? source, { ...init, headers }));
      return nativeFetch(input, { ...init, headers });
    };

    const response = await send(0);
    if (key && response.status === 409) {
      const data = await response.clone().json().catch(() => null);
      if (isStillRunning(409, data)) {
        await pause(800);
        headers.delete('x-integrity-token');
        headers.delete('x-integrity-nonce');
        headers.delete('x-turnstile-token');
        await addIntegrity();
        return send(1);
      }
    }
    return response;
  };
}
